import { getPercentageDiff } from './get-percentage-diff';
import { convertToLocale } from './money';

type VariantWithPrice = {
  id?: string | null;
  sku?: string | null;
  calculated_price?: {
    calculated_amount: number | null;
    original_amount: number | null;
    currency_code: string | null;
    calculated_price?: { price_list_type: string | null } | null;
  } | null;
};

type ProductForPricing = {
  id?: string | null;
  variants?: VariantWithPrice[] | null;
};

export const getPricesForVariant = (
  variant: VariantWithPrice | null | undefined,
) => {
  if (variant?.calculated_price?.calculated_amount == null) {
    return null;
  }

  const calcAmount = variant.calculated_price.calculated_amount;
  const origAmount = variant.calculated_price.original_amount;
  const currencyCode = variant.calculated_price.currency_code;

  if (currencyCode == null) {
    return null;
  }

  return {
    calculated_price_number: calcAmount,
    calculated_price: convertToLocale({
      amount: calcAmount,
      currency_code: currencyCode,
    }),
    original_price_number: origAmount ?? 0,
    original_price: convertToLocale({
      amount: origAmount ?? 0,
      currency_code: currencyCode,
    }),
    currency_code: currencyCode,
    price_type:
      variant.calculated_price.calculated_price?.price_list_type ?? '',
    percentage_diff:
      origAmount != null && origAmount > 0
        ? getPercentageDiff(origAmount, calcAmount)
        : '0',
  };
};

function findCheapestVariant(variants: VariantWithPrice[]) {
  let cheapest: VariantWithPrice | null = null;
  let hasMultiplePrices = false;

  for (const v of variants) {
    const variant = v as VariantWithPrice;
    if (!variant.calculated_price) {
      continue;
    }

    const amount = variant.calculated_price.calculated_amount ?? 0;

    if (!cheapest) {
      cheapest = variant;
      continue;
    }

    // Stryker disable next-line OptionalChaining: `cheapest` is only assigned
    // from a variant where `calculated_price` has already been verified by the
    // outer guard, so `cheapest.calculated_price?` is provably non-null here.
    const cheapestAmount = cheapest.calculated_price?.calculated_amount ?? 0;

    if (amount !== cheapestAmount) {
      hasMultiplePrices = true;
    }

    // Stryker disable next-line EqualityOperator: `<` vs `<=` is equivalent —
    // on a tie the reassignment writes the same VariantWithPrice value back
    // to `cheapest`, so the eventual minimum is unchanged.
    if (amount < cheapestAmount) {
      cheapest = variant;
    }
  }

  return { cheapest, hasMultiplePrices };
}

export function getProductPrice({
  product,
  variantId,
}: {
  product: ProductForPricing;
  variantId?: string;
}) {
  if (!product?.id) {
    throw new Error('No product provided');
  }

  const cheapestPrice = () => {
    if (!product?.variants?.length) {
      return null;
    }

    const { cheapest, hasMultiplePrices } = findCheapestVariant(
      product.variants,
    );
    const result = getPricesForVariant(cheapest);

    if (!result) {
      return null;
    }

    return { ...result, has_multiple_prices: hasMultiplePrices };
  };

  const variantPrice = () => {
    if (!product || !variantId) {
      return null;
    }

    const variant = product.variants?.find(
      (v) => v.id === variantId || v.sku === variantId,
    );

    if (!variant) {
      return null;
    }

    return getPricesForVariant(variant);
  };

  return {
    product,
    cheapestPrice: cheapestPrice(),
    variantPrice: variantPrice(),
  };
}
